import { CacheAction, useCacheService } from 'framework/cache';
import { CrudService, CrudServiceProvider, CrudServiceProviderParams } from './crud.types';
import { ResourceData, ResourceId } from './crud.types';

interface CachedCrudService<
  TEntity = any,
  TSearchResult = any,
  TSearchAllResult = TEntity[],
  TFindAllResult = TEntity[],
> extends CrudService<TEntity, TSearchResult, TSearchAllResult, TFindAllResult> {
  invalidateCache: () => CacheAction;
  addToCache: (id: ResourceId, entityData: TEntity) => CacheAction;
}

const useCachedCrudService = <
  TEntity = any,
  TSearchResult = any,
  TSearchAllResult = TEntity[],
  TFindAllResult = TEntity[],
>(
  useParentCrudService: CrudServiceProvider<TEntity, TSearchResult, TSearchAllResult, TFindAllResult>,
  { listResourceUrl, singleResourceUrl, useService, entityName }: CrudServiceProviderParams,
): CachedCrudService<TEntity, TSearchResult, TSearchAllResult, TFindAllResult> => {
  const { addToCache, replaceCache, getValue, invalidateCache } = useCacheService<TEntity>(listResourceUrl);

  const {
    create: parentCreate,
    getById: parentGetById,
    update: parentUpdate,
    deleteById: parentDeleteById,
    ...rest
  } = useParentCrudService({ listResourceUrl, singleResourceUrl, useService, entityName });

  const getById = async (id: ResourceId, ignoreCache = false) => {
    const cachedValue = getValue(id);
    if (!ignoreCache && cachedValue) {
      return cachedValue;
    } else {
      const entityData = await parentGetById(id);
      addToCache(id, entityData);
      return entityData;
    }
  };

  const update = async (id: ResourceId, request: ResourceData) => {
    const entityData = await parentUpdate(id, request);
    replaceCache(id, entityData);
    return entityData;
  };

  const create = async (request: ResourceData) => {
    const entityData = await parentCreate(request);
    addToCache((entityData as ResourceData).id, entityData);
    return entityData;
  };

  const deleteById = async (id: ResourceId) => {
    await parentDeleteById(id);
    invalidateCache();
  };

  return {
    create,
    getById,
    update,
    deleteById,
    ...rest,
    invalidateCache,
    addToCache,
  };
};

export default useCachedCrudService;
